import React from "react";
import {
  FeatherAlertTriangle,
  FeatherCheckCircle,
  FeatherCloudOff,
  FeatherFileText,
  FeatherPlay,
  FeatherUsers,
} from "@subframe/core";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { GstChecks } from "../../domain/entities/CompanySettings";

interface GstChecksSectionProps {
  checks: GstChecks | null;
  running: boolean;
  error: string | null;
  onRun: () => void;
}

/** One check's result: a heading, a sentence, and the list it found. */
const CheckResult: React.FC<{
  icon: React.ReactNode;
  title: string;
  message: string;
  clean: boolean;
  truncated: boolean;
  children?: React.ReactNode;
}> = ({ icon, title, message, clean, truncated, children }) => (
  <div className="flex w-full flex-col gap-3 rounded-md border border-solid border-neutral-border px-4 py-4">
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-body-bold font-body-bold text-default-font">{title}</span>
      {clean ? (
        <Badge variant="success" icon={<FeatherCheckCircle />}>
          Nothing to fix
        </Badge>
      ) : (
        <Badge variant="warning" icon={<FeatherAlertTriangle />}>
          Needs a look
        </Badge>
      )}
    </div>

    <span className="text-body font-body text-subtext-color">{message}</span>

    {children}

    {truncated && (
      <span className="text-caption font-caption text-subtext-color">
        Only the first 100 are listed. Fix these and run the check again to see the rest.
      </span>
    )}
  </div>
);

/**
 * ZOUR-19 — the two on-demand checks on `/settings/gst`.
 *
 * REPORT ONLY, and the copy says so in both results. Neither one offers a fix
 * button, deliberately:
 *
 *  - Past documents are historical records. The push path already strips the
 *    tax when each one goes to the books; rewriting a client's history from a
 *    settings page is not a button anybody should have.
 *  - A contact's GST treatment is a judgement about who that party is
 *    (`business_gst` / `business_none` / `consumer` / `overseas`), and guessing
 *    it wrong quietly mis-taxes every future document with them.
 *
 * The contacts checks are the ones that save real pain: Zoho makes GST
 * Treatment mandatory on contacts once the org is registered, so a blank one
 * makes the NEXT document against that party fail at push time — the worst
 * moment to find out.
 *
 * There are TWO of them, and they are not redundant. The second reads
 * Zournal's own column; the third reads ZOHO'S, because a contact created
 * while the company was still non-GST was pushed with its GST fields stripped
 * and is blank in Zoho while Zournal's column looks perfectly fine. That is the
 * migration case, and only Zoho can answer it.
 */
export const GstChecksSection: React.FC<GstChecksSectionProps> = ({
  checks,
  running,
  error,
  onRun,
}) => (
  <section className="flex w-full flex-col gap-4 border-b border-solid border-neutral-border pb-8 last:border-b-0 last:pb-0">
    <div className="flex w-full items-start justify-between gap-4">
      <div className="flex flex-col gap-1">
        <span className="text-heading-3 font-heading-3 text-default-font">Checks</span>
        <span className="text-body font-body text-subtext-color">
          Run these after switching GST on to find documents and contacts that need attention.
          They only report. Nothing here changes your documents, your masters or your books.
        </span>
      </div>

      <Button
        variant="neutral-secondary"
        icon={<FeatherPlay />}
        loading={running}
        disabled={running}
        onClick={onRun}
      >
        {checks ? "Run again" : "Run checks"}
      </Button>
    </div>

    {error && (
      <div className="flex w-full items-start gap-2 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-3">
        <FeatherAlertTriangle className="mt-0.5 shrink-0 text-body font-body text-error-700" />
        <span className="text-body font-body text-error-700">{error}</span>
      </div>
    )}

    {checks && (
      <div className="flex w-full flex-col gap-4">
        <CheckResult
          icon={<FeatherFileText className="shrink-0 text-body font-body text-subtext-color" />}
          title="Past invoices and bills that still carry GST"
          message={checks.pastDocuments.message}
          clean={checks.pastDocuments.documents.length === 0}
          truncated={checks.pastDocuments.truncated}
        >
          {checks.pastDocuments.documents.length > 0 && (
            <>
              <div className="flex w-full flex-col overflow-x-auto rounded-md border border-solid border-neutral-border">
                {checks.pastDocuments.documents.map((doc) => (
                  <div
                    key={`${doc.kind}-${doc.id}`}
                    className="flex items-center gap-4 border-b border-solid border-neutral-border px-4 py-2 last:border-b-0"
                  >
                    <span className="w-16 flex-none text-caption font-caption text-subtext-color">
                      {doc.kind === "bill" ? "Bill" : "Invoice"}
                    </span>
                    <span className="flex-1 text-body font-body text-default-font">
                      {doc.documentNumber || `#${doc.id}`}
                    </span>
                    <span className="flex-none text-caption font-caption text-subtext-color">
                      {doc.date ?? ""}
                    </span>
                  </div>
                ))}
              </div>

              {/* Listed rather than linked, on purpose. The review pages are
                  opened with the whole document handed over in router state
                  (see AllBillsPage.handleReviewClick), not by an id in the
                  URL — a link from here would land on an empty review page.
                  Giving an id-addressable route to those pages is a real
                  change to them and is not part of ZOUR-19, so this says where
                  to look instead of pretending to be a link. */}
              <span className="text-caption font-caption text-subtext-color">
                Open these from Bills or Invoices, searching for the document number above.
              </span>
            </>
          )}
        </CheckResult>

        <CheckResult
          icon={<FeatherUsers className="shrink-0 text-body font-body text-subtext-color" />}
          title="Customers and vendors with no GST treatment"
          message={checks.contacts.message}
          clean={checks.contacts.contacts.length === 0}
          truncated={checks.contacts.truncated}
        >
          {checks.contacts.contacts.length > 0 && (
            <div className="flex w-full flex-col overflow-x-auto rounded-md border border-solid border-neutral-border">
              {checks.contacts.contacts.map((contact) => (
                <a
                  key={`${contact.kind}-${contact.id}`}
                  href="/settings/customers-vendors"
                  className="flex items-center gap-4 border-b border-solid border-neutral-border px-4 py-2 last:border-b-0 hover:bg-neutral-50"
                >
                  <span className="w-16 flex-none text-caption font-caption text-subtext-color">
                    {contact.kind === "vendor" ? "Vendor" : "Customer"}
                  </span>
                  <span className="flex-1 text-body font-body text-default-font">
                    {contact.name || `#${contact.id}`}
                  </span>
                </a>
              ))}
            </div>
          )}
        </CheckResult>
        {checks.zohoContacts && (
          <CheckResult
            icon={<FeatherCloudOff className="shrink-0 text-body font-body text-subtext-color" />}
            title="GST treatment missing in Zoho Books"
            message={checks.zohoContacts.message}
            clean={
              checks.zohoContacts.checked && checks.zohoContacts.contacts.length === 0
            }
            truncated={checks.zohoContacts.truncated}
          >
            {checks.zohoContacts.contacts.length > 0 && (
              <div className="flex w-full flex-col overflow-x-auto rounded-md border border-solid border-neutral-border">
                {checks.zohoContacts.contacts.map((contact) => (
                  <a
                    key={`zoho-${contact.kind}-${contact.id}`}
                    href="/settings/customers-vendors"
                    className="flex items-center gap-4 border-b border-solid border-neutral-border px-4 py-2 last:border-b-0 hover:bg-neutral-50"
                  >
                    <span className="w-16 flex-none text-caption font-caption text-subtext-color">
                      {contact.kind === "vendor" ? "Vendor" : "Customer"}
                    </span>
                    <span className="flex-1 text-body font-body text-default-font">
                      {contact.name || `#${contact.id}`}
                    </span>
                    <span className="flex-none text-caption font-caption text-subtext-color">
                      {contact.localTreatment} here, blank in Zoho
                    </span>
                  </a>
                ))}
              </div>
            )}
          </CheckResult>
        )}
      </div>
    )}
  </section>
);
